/* Subvest EZ pricing preview. Owns only Dynamic Clothing add/edit/checkout paths.
   Does not alter EZ definitions. Unknown widget shapes and uncertain writes fail closed. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory;
  else factory(root).start();
})(typeof window === 'undefined' ? globalThis : window, function (w) {
  'use strict';
  const d = w.document;
  const config = w.SubvestEZGuardConfig || {};
  const ADDON = 45274057900283;
  const VERSION = 'preview-1';
  const STORE_KEY = 'subvest-ez-pending-v1';
  let busy = false;
  const isDynamic = item => /dynamic clothing/i.test(item.product_title || item.title || '');
  const cents = value => {
    if (!/^\d+(\.\d{1,2})?$/.test(String(value))) throw Error('An option price could not be verified. Please refresh before adding.');
    return Math.round(Number(value) * 100);
  };
  const stop = event => { event.preventDefault(); event.stopImmediatePropagation(); };
  const url = path => (w.Shopify?.routes?.root || '/') + path;
  const pending = () => JSON.parse(w.sessionStorage.getItem(STORE_KEY) || 'null');
  const save = value => value ? w.sessionStorage.setItem(STORE_KEY, JSON.stringify(value)) : w.sessionStorage.removeItem(STORE_KEY);
  const group = (cart, id) => cart.items.filter(i => i.properties?.__additional_price_id === id);

  function notice(message, error = false) {
    let box = d.getElementById('subvest-ez-pricing-status');
    if (!box) {
      box = d.createElement('div');
      box.id = 'subvest-ez-pricing-status';
      box.setAttribute('role', 'status');
      box.setAttribute('aria-live', 'polite');
      box.style.cssText = 'padding:12px;margin:12px 0;border:1px solid #667780;background:#f5f7f8;color:#172b3b;';
      const host = d.querySelector('product-form') || d.querySelector('#main-cart-footer') || d.querySelector('main');
      (host || d.body).prepend(box);
    }
    box.textContent = message;
    box.setAttribute('role', error ? 'alert' : 'status');
    if (error) box.scrollIntoView?.({ block: 'nearest' });
  }

  function quote(form) {
    const widget = d.getElementById('ez-ds-option-widget');
    if (!widget || !widget.querySelector('.inp-prod-title')) throw Error('Customization options are still loading. Please try again once they appear.');
    const all = Array.from(widget.querySelectorAll('.inp-prod-input-fo select, .inp-prod-input-fo input[name^="properties["], .inp-prod-input-fo textarea'));
    // All 406 audited Dynamic Clothing definitions share this shape. Fail closed if it changes.
    if (all.length !== 7 || all.filter(e => e.tagName === 'SELECT').length !== 4 || all.filter(e => e.closest('.dropdown-w-image')).length !== 2) {
      throw Error('This customization layout needs a pricing compatibility check. Nothing has been added.');
    }
    const options = [];
    for (const el of all) {
      if (el.disabled || el.closest('.invisible-itm')) continue;
      if (!el.checkValidity()) { el.reportValidity(); throw Error('Please complete the required customization options.'); }
      let raw;
      if (el.tagName === 'SELECT') {
        if (el.multiple || !el.selectedOptions[0]) throw Error('Please select one option.');
        raw = el.selectedOptions[0].dataset.price;
      } else if (el.closest('.dropdown-w-image')) {
        const matches = Array.from(el.closest('.dropdown-w-image').querySelectorAll('.dwi-list li')).filter(li => li.dataset.value === el.dataset.value);
        if (matches.length !== 1) throw Error('Please reselect the image customization before adding.');
        raw = matches[0].dataset.price;
        const expectedValue = matches[0].dataset.value + (Number(raw) > 0 ? ' (+$' + raw + ')' : '');
        if (el.value !== expectedValue) throw Error('An image selection is still updating. Please select it again.');
      } else {
        if (!['text', 'number', 'date', 'email'].includes(el.type)) throw Error('This customization type needs a pricing compatibility check.');
        raw = el.value ? el.dataset.price : '0';
      }
      const amount = cents(raw);
      const match = el.name.match(/^properties\[(.*)\]$/);
      if (!match) throw Error('The selected options could not be read.');
      // Remove only the app's previously appended currency annotation, retaining the merchant's label.
      const name = match[1].replace(/(?: \(\+\$\d+(?:\.\d+)?\))+$/, '');
      options.push({ name: name + (amount && !el.closest('.dropdown-w-image') ? ' (+$' + amount / 100 + ')' : ''), value: el.value, cents: amount });
    }
    const total = options.reduce((sum, item) => sum + item.cents, 0);
    if (total % 100 !== 0) throw Error('This option cannot be represented by the existing surcharge product. Nothing has been added.');
    const fd = new w.FormData(form);
    const variantId = Number(fd.get('id'));
    const variant = (config.variants || []).find(v => v.id === variantId);
    const quantity = Number(fd.get('quantity') || 1);
    if (!variant || !Number.isSafeInteger(quantity) || quantity < 1) throw Error('Please check the garment and quantity.');
    const properties = Object.fromEntries(options.map(o => [o.name, o.value]));
    if (Object.keys(properties).length !== options.length) throw Error('Duplicate customization fields require review.');
    return { variantId, productId: config.productId, baseCents: variant.price, quantity, options, properties, cents: total };
  }

  function audit(cart) {
    const errors = [];
    const garments = cart.items.filter(isDynamic);
    const used = new Set();
    for (const item of garments) {
      const p = item.properties || {};
      let q;
      try { q = JSON.parse(p.__subvest_ez_quote); } catch (_) {}
      if (p.__subvest_ez_guard !== VERSION || !q || !Array.isArray(q.options)) {
        errors.push('Please remove and re-add ' + item.product_title + ' from its product page so all customization charges can be verified.');
        continue;
      }
      const id = p.__additional_price_id;
      if (!id || used.has(id)) { errors.push('A customization group is duplicated or incomplete.'); continue; }
      used.add(id);
      const expected = q.options.reduce((sum, o) => sum + (Number.isSafeInteger(o.cents) && o.cents >= 0 ? o.cents : NaN), 0);
      if (!Number.isSafeInteger(expected) || expected % 100 || Number(p.__additional_price_total) * 100 !== expected || q.options.some(o => p[o.name] !== o.value)) {
        errors.push('Selected customizations and their quoted charges disagree.'); continue;
      }
      const siblings = group(cart, id);
      const addons = siblings.filter(i => i.variant_id === ADDON);
      const required = expected / 100 * item.quantity;
      if (siblings.some(i => i !== item && i.variant_id !== ADDON) || (required === 0 ? addons.length !== 0 : addons.length !== 1 || addons[0].quantity !== required || addons[0].original_price !== 100 || Number(addons[0].properties.__additional_price_total) * 100 !== expected)) {
        errors.push('A customization charge is missing, duplicated, or has the wrong quantity.');
      }
    }
    for (const item of cart.items) {
      if (item.variant_id === ADDON && item.properties?.__subvest_ez_guard === VERSION && !used.has(item.properties.__additional_price_id)) errors.push('An orphaned customization charge needs to be removed.');
    }
    return errors;
  }

  async function request(path, body) {
    const controller = new w.AbortController();
    const timer = w.setTimeout(() => controller.abort(), 20000);
    try {
      const res = await w.fetch(url(path), { method: body ? 'POST' : 'GET', credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json', ...(body ? { 'Content-Type': 'application/json' } : {}) }, ...(body ? { body: JSON.stringify(body) } : {}), signal: controller.signal });
      let data; try { data = await res.json(); } catch (_) { throw Error('The cart response could not be verified.'); }
      if (!res.ok) { const error = Error(data.description || data.message || 'The cart request failed.'); error.status = res.status; throw error; }
      return data;
    } finally { w.clearTimeout(timer); }
  }
  const read = () => request('cart.js');
  function complete(cart, op) {
    const items = group(cart, op.id);
    const base = items.find(i => i.variant_id === op.variantId);
    return !!base && base.quantity === op.quantity && base.original_price === op.baseCents && audit(cart).length === 0;
  }
  function goCart() { w.location.assign(url('cart')); }
  async function removeOperation(cart, op) {
    const updates = Object.fromEntries(group(cart, op.id).map(i => [i.key, 0]));
    if (Object.keys(updates).length) await request('cart/update.js', { updates });
    if (group(await read(), op.id).length) throw Error('The incomplete addition could not be removed. Checkout is blocked.');
  }
  async function recover(op) {
    const cart = await read();
    if (complete(cart, op)) { save(null); goCart(); return; }
    throw Error('The previous addition has an uncertain result. Checkout and retries remain blocked to prevent duplicate charges. Reload to check it again; do not add the same item in another tab.');
  }
  async function add(form) {
    if (busy) return;
    busy = true;
    notice('Checking customization prices…');
    let op;
    let submitted = false;
    try {
      if (pending()) { await recover(pending()); return; }
      const q = quote(form);
      const existing = await read();
      const problems = audit(existing);
      if (problems.length) throw Error(problems[0]);
      // Re-read after the async cart read: a customer may have changed options in the meantime.
      const fresh = quote(form);
      if (JSON.stringify(q) !== JSON.stringify(fresh)) throw Error('Your options changed while prices were being checked. Please press Add to cart again.');
      op = { id: 'sv-' + w.crypto.randomUUID(), ...q };
      const props = { ...q.properties, __additional_price_id: op.id, __additional_price_total: String(q.cents / 100), __subvest_ez_guard: VERSION, __subvest_ez_quote: JSON.stringify({ options: q.options }) };
      const items = [{ id: q.variantId, quantity: q.quantity, properties: props }];
      if (q.cents) items.push({ id: ADDON, quantity: q.cents / 100 * q.quantity, properties: { __additional_price_id: op.id, __additional_price_total: String(q.cents / 100), __subvest_ez_guard: VERSION } });
      save(op);
      submitted = true;
      notice('Adding the garment and its customization charges…');
      await request('cart/add.js', { items });
      const after = await read();
      if (!complete(after, op)) {
        await removeOperation(after, op);
        save(null);
        throw Error('The complete garment and customization charge could not be added. This attempt was removed; please try again.');
      }
      save(null);
      goCart();
    } catch (error) {
      if (submitted && pending() && error.status >= 400 && error.status < 500) {
        try { await removeOperation(await read(), op); save(null); } catch (_) {}
      }
      if (submitted && pending()) notice('The cart request could not be confirmed. Checkout is blocked. Press Add to cart again only to check the previous attempt; it will not add a duplicate.', true);
      else notice(error.message, true);
    } finally { busy = false; }
  }

  async function checkout() {
    if (busy) { notice('Please wait until the customization charges have been confirmed.'); return; }
    busy = true;
    try {
      if (pending()) throw Error('A previous addition is still unconfirmed. Return to the product page to recover it before checkout.');
      notice('Verifying customization charges before checkout…');
      const problems = audit(await read());
      if (problems.length) throw Error(problems[0]);
      w.location.assign(url('checkout'));
    } catch (error) { notice(error.message, true); }
    finally { busy = false; }
  }

  function lineFromElement(el) {
    const row = el.closest('.cart-item');
    const link = row?.querySelector('a[href*="/cart/change"]');
    if (!link) return null;
    return new URL(link.href, w.location.href).searchParams.get('id');
  }
  function isGuardRow(el) {
    const row = el.closest('.cart-item');
    return row && /dynamic clothing/i.test(row.textContent);
  }
  async function change(key, quantity) {
    if (busy) return;
    busy = true;
    try {
      if (!key || !Number.isSafeInteger(quantity) || quantity < 0) throw Error('Please enter a valid quantity.');
      if (pending()) throw Error('Resolve the pending addition before editing this cart.');
      const before = await read();
      const item = before.items.find(i => i.key === key);
      if (!item || !isDynamic(item)) throw Error('The cart changed. Refresh it before editing.');
      const p = item.properties || {};
      const siblings = p.__additional_price_id ? group(before, p.__additional_price_id) : [item];
      if (quantity && audit(before).length) throw Error(audit(before)[0]);
      const updates = { [item.key]: quantity };
      for (const addon of siblings.filter(i => i.variant_id === ADDON)) updates[addon.key] = quantity * Number(p.__additional_price_total);
      await request('cart/update.js', { updates });
      const after = await read();
      const invalid = quantity
        ? after.items.find(i => i.key === key)?.quantity !== quantity || audit(after).length
        : siblings.some(sibling => after.items.some(i => i.key === sibling.key));
      // Allow removal of an old/unverified item even if another old item still needs removal.
      if (invalid) throw Error('The quantity update could not be verified. Checkout is blocked until the cart is corrected.');
      w.location.reload();
    } catch (error) { notice(error.message, true); }
    finally { busy = false; }
  }
  function isAdd(button) { return config.target && button?.matches('button[name="add"],input[name="add"],button.product-form__submit'); }
  function start() {
    w.SubvestEZGuard = { version: VERSION };
    w.addEventListener('click', event => {
      const button = event.target.closest?.('button,input[type="submit"],a');
      if (isAdd(button)) { stop(event); add(button.form || button.closest('form')); return; }
      if (button?.matches('[name="checkout"],a[href^="/checkout"],a[href*="/checkouts/"]')) { stop(event); checkout(); return; }
      if (button?.closest('cart-remove-button') && isGuardRow(button)) { stop(event); change(lineFromElement(button), 0); }
    }, true);
    w.addEventListener('submit', event => {
      if (config.target && event.target.matches('form[action*="/cart/add"]')) { stop(event); add(event.target); }
      else if (event.submitter?.name === 'checkout') { stop(event); checkout(); }
    }, true);
    w.addEventListener('change', event => {
      if (event.target.matches('input[name="updates[]"]') && isGuardRow(event.target)) { stop(event); change(lineFromElement(event.target), Number(event.target.value)); }
    }, true);
    if (config.target || config.cartHasDynamic) {
      const style = d.createElement('style');
      style.textContent = '.shopify-payment-button,.additional-checkout-buttons,.cart__dynamic-checkout-buttons{display:none!important}';
      // The animated information wrapper otherwise leaves open EZ image menus behind the description.
      if (config.target) style.textContent += '.product__info-wrapper{position:relative;z-index:2}';
      d.head.append(style);
    }
  }
  return { start, quote, audit, add, checkout, change, version: VERSION };
});
